// File: src/pages/HomePage.tsx
import { Link } from 'react-router-dom';
import { useI18n } from '../i18n/I18nContext';
import { useLocalizedPath } from '../i18n/localeRouting';

export function HomePage() {
  const { t } = useI18n();
  const lp = useLocalizedPath();

  return (
    <div className="min-h-screen bg-gradient-to-b from-page via-surface to-page -mt-8 -mx-4 px-4 py-12">
      <div className="max-w-6xl mx-auto">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-5xl md:text-6xl font-bold font-heading text-primary mb-6">
            {t('homeTitle')}
          </h1>
          <p className="text-xl md:text-2xl text-secondary mb-8 max-w-3xl mx-auto">
            {t('homeSubtitle')}
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link
              to={lp('/lookup')}
              className="bg-validator hover:bg-validator-hover text-white font-bold py-3 px-8 rounded-btn text-lg transition-colors shadow-lg"
            >
              {t('homeCtaGetStarted')}
            </Link>
            <a
              href="#features"
              className="bg-surface hover:bg-hover text-primary font-bold py-3 px-8 rounded-btn text-lg transition-colors shadow-lg border-2 border-input-border"
            >
              {t('homeCtaLearnMore')}
            </a>
          </div>
        </div>

        {/* Features Section */}
        <div id="features" className="mb-16">
          <h2 className="text-3xl font-bold font-heading text-center text-primary mb-12">{t('homeSectionToolsTitle')}</h2>

          {/* Main Tools */}
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            {/* Word Validator */}
            <Link to={lp('/lookup')} className="bg-surface/80 backdrop-blur-theme rounded-card border border-subtle shadow-card p-8 card-interactive transition-all">
              <div className="w-14 h-14 bg-validator-subtle rounded-xl flex items-center justify-center text-2xl mb-4">✓</div>
              <h3 className="text-xl font-bold text-primary mb-3">{t('homeToolValidatorTitle')}</h3>
              <p className="text-secondary mb-4">{t('homeToolValidatorDesc')}</p>
              <div className="text-validator-text font-semibold">{t('homeToolCtaTryNow')} →</div>
            </Link>

            {/* Word Finder */}
            <Link to={lp('/word-finder')} className="bg-surface/80 backdrop-blur-theme rounded-card border border-subtle shadow-card p-8 card-interactive transition-all">
              <div className="w-14 h-14 bg-finder-subtle rounded-xl flex items-center justify-center text-2xl mb-4">🔍</div>
              <h3 className="text-xl font-bold text-primary mb-3">{t('homeToolFinderTitle')}</h3>
              <p className="text-secondary mb-4">{t('homeToolFinderDesc')}</p>
              <div className="text-finder-text font-semibold">{t('homeToolCtaExplore')} →</div>
            </Link>

            {/* Anagram Solver */}
            <Link to={lp('/anagram-solver')} className="bg-surface/80 backdrop-blur-theme rounded-card border border-subtle shadow-card p-8 card-interactive transition-all">
              <div className="w-14 h-14 bg-anagram-subtle rounded-xl flex items-center justify-center text-2xl mb-4">🎲</div>
              <h3 className="text-xl font-bold text-primary mb-3">{t('homeToolAnagramTitle')}</h3>
              <p className="text-secondary mb-4">{t('homeToolAnagramDesc')}</p>
              <div className="text-anagram-text font-semibold">{t('homeToolCtaSolve')} →</div>
            </Link>
          </div>

          {/* AI-Powered & Game Features */}
          <div className="grid md:grid-cols-2 gap-8">
            {/* Magic Search */}
            <Link to={lp('/magic')} className="bg-magic-subtle rounded-card border border-magic/30 shadow-card p-8 card-interactive transition-all">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 bg-magic-subtle rounded-xl flex items-center justify-center text-2xl">✨</div>
                <span className="bg-magic text-white text-sm font-bold px-3 py-1 rounded-full">{t('homeAIPowered')}</span>
              </div>
              <h3 className="text-xl font-bold text-primary mb-3">{t('homeMagicTitle')}</h3>
              <p className="text-secondary mb-4">{t('homeMagicDesc')}</p>
              <div className="text-magic-text font-semibold">{t('homeMagicCta')} →</div>
            </Link>

            {/* Practice Game */}
            <Link to={lp('/practice')} className="bg-practice-subtle rounded-card border border-practice/30 shadow-card p-8 card-interactive transition-all">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 bg-practice-subtle rounded-xl flex items-center justify-center text-2xl">🎮</div>
                <span className="bg-practice text-white text-sm font-bold px-3 py-1 rounded-full">{t('homeGameMode')}</span>
              </div>
              <h3 className="text-xl font-bold text-primary mb-3">{t('homePracticeTitle')}</h3>
              <p className="text-secondary mb-4">{t('homePracticeDesc')}</p>
              <div className="text-practice-text font-semibold">{t('homePracticeCta')} →</div>
            </Link>
          </div>
        </div>

        {/* Key Features Section */}
        <div className="bg-surface-inv rounded-card shadow-lg p-8 mb-16 text-inv-text border border-subtle">
          <h2 className="text-3xl font-bold font-heading text-center mb-8">{t('homeHighlightsTitle')}</h2>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <div className="flex items-start gap-3">
              <div className="text-2xl">📚</div>
              <div>
                <h3 className="font-bold mb-1">{t('homeHighlightDictionaryTitle')}</h3>
                <p className="text-inv-secondary text-sm">{t('homeHighlightDictionaryDesc')}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="text-2xl">🚀</div>
              <div>
                <h3 className="font-bold mb-1">{t('homeHighlightPerformanceTitle')}</h3>
                <p className="text-inv-secondary text-sm">{t('homeHighlightPerformanceDesc')}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="text-2xl">🎯</div>
              <div>
                <h3 className="font-bold mb-1">{t('homeHighlightFilteringTitle')}</h3>
                <p className="text-inv-secondary text-sm">{t('homeHighlightFilteringDesc')}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="text-2xl">📱</div>
              <div>
                <h3 className="font-bold mb-1">{t('homeHighlightMobileTitle')}</h3>
                <p className="text-inv-secondary text-sm">{t('homeHighlightMobileDesc')}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="grid md:grid-cols-4 gap-6 mb-16">
          <div className="bg-surface/80 backdrop-blur-theme rounded-card border border-subtle shadow-card p-6 text-center card-interactive transition-all">
            <div className="text-4xl font-bold text-validator-text mb-2">370K+</div>
            <div className="text-secondary">{t('homeStatsWords')}</div>
          </div>
          <div className="bg-surface/80 backdrop-blur-theme rounded-card border border-subtle shadow-card p-6 text-center card-interactive transition-all">
            <div className="text-4xl font-bold text-magic-text mb-2">5</div>
            <div className="text-secondary">{t('homeStatsTools')}</div>
          </div>
          <div className="bg-surface/80 backdrop-blur-theme rounded-card border border-subtle shadow-card p-6 text-center card-interactive transition-all">
            <div className="text-4xl font-bold text-finder-text mb-2">&lt;1s</div>
            <div className="text-secondary">{t('homeStatsSpeed')}</div>
          </div>
          <div className="bg-surface/80 backdrop-blur-theme rounded-card border border-subtle shadow-card p-6 text-center card-interactive transition-all">
            <div className="text-4xl font-bold text-practice-text mb-2">EN/ES</div>
            <div className="text-secondary">{t('homeStatsBilingual')}</div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-surface/80 backdrop-blur-theme rounded-card border border-subtle shadow-card p-12 text-center">
          <h2 className="text-3xl font-bold font-heading text-primary mb-4">{t('homeCtaTitle')}</h2>
          <p className="text-xl text-secondary mb-8 max-w-2xl mx-auto">
            {t('homeCtaSubtitle')}
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link
              to={lp('/lookup')}
              className="inline-flex items-center justify-center rounded-btn px-8 py-3 text-lg font-semibold border-2 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-validator border-validator/40 bg-validator-subtle text-validator-text hover:bg-validator hover:text-white"
            >
              {t('navValidator')}
            </Link>
            <Link
              to={lp('/word-finder')}
              className="inline-flex items-center justify-center rounded-btn px-8 py-3 text-lg font-semibold border-2 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-finder border-finder/30 bg-finder-subtle text-finder-text hover:bg-finder hover:text-white"
            >
              {t('navFinder')}
            </Link>
            <Link
              to={lp('/anagram-solver')}
              className="inline-flex items-center justify-center rounded-btn px-8 py-3 text-lg font-semibold border-2 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-anagram border-anagram/30 bg-anagram-subtle text-anagram-text hover:bg-anagram hover:text-white"
            >
              {t('navAnagram')}
            </Link>
            <Link
              to={lp('/magic')}
              className="inline-flex items-center justify-center rounded-btn px-8 py-3 text-lg font-semibold border-2 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-magic border-magic/30 bg-magic-subtle text-magic-text hover:bg-magic hover:text-white"
            >
              ✨ {t('navMagic')}
            </Link>
            <Link
              to={lp('/practice')}
              className="inline-flex items-center justify-center rounded-btn px-8 py-3 text-lg font-semibold border-2 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-practice border-practice/30 bg-practice-subtle text-practice-text hover:bg-practice hover:text-white"
            >
              {t('navPractice')}
            </Link>
          </div>
        </div>

        {/* Footer Note */}
        <div className="text-center mt-12 text-muted">
          <p className="text-sm">
            {t('homeFooterNote')}
          </p>
        </div>
      </div>
    </div>
  );
}
